import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AthletePicker from "@/components/AthletePicker";

/**
 * InviteAthletesDialog — Convocazione atleti a una gara.
 * Usa AthletePicker con preset derivati dalla gara (arma, categoria).
 * L'utente può modificare i filtri.
 *
 * Alla conferma, genera invited_weapons combinando ogni atleta con:
 *  - la weapon della gara, se l'atleta pratica quell'arma
 *  - altrimenti la sua arma primaria (o la prima disponibile)
 */
export default function InviteAthletesDialog({ competition, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [weaponsMap, setWeaponsMap] = useState({});
  const [initialIds, setInitialIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const ids = Array.from(new Set([
      ...(competition?.invited_athlete_ids || []),
      ...(competition?.invited_weapons || []).map((w) => w.athlete_id),
    ].filter(Boolean)));
    setInitialIds(ids);
    api.get("/athletes-with-weapons").then((r) => {
      const wm = {};
      (r.data || []).forEach((a) => { wm[a.id] = (a.weapons || []).filter((w) => w.status === "attiva" || w.status === "secondaria"); });
      setWeaponsMap(wm);
    });
  }, [open, competition?.id]);

  const preset = {
    weaponCode: competition?.weapon || "all",
    categoryCode: competition?.category || "all",
  };

  const handleConfirm = async (ids) => {
    setSaving(true);
    try {
      const invited_weapons = [];
      const compWeapon = competition?.weapon;
      ids.forEach((aid) => {
        const ws = weaponsMap[aid] || [];
        let code = compWeapon && ws.some((w) => w.weapon_code === compWeapon) ? compWeapon : null;
        if (!code) {
          const primary = ws.find((w) => w.is_primary) || ws[0];
          code = primary?.weapon_code || compWeapon || null;
        }
        if (code) invited_weapons.push({ athlete_id: aid, weapon_code: code, status: "convocato" });
      });
      await api.put(`/competitions/${competition.id}`, {
        ...competition,
        invited_athlete_ids: ids,
        invited_weapons,
      });
      toast.success(`${ids.length} atleti convocati`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio convocazioni");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}>
        {trigger || <Button variant="outline" size="sm"><UserPlus className="w-3.5 h-3.5 mr-1" />Convoca</Button>}
      </div>
      <AthletePicker
        open={open}
        onOpenChange={setOpen}
        title={`Convoca atleti · ${competition?.name || ""}`}
        description="Suggerimenti applicati automaticamente in base a arma e categoria della gara. Puoi modificarli."
        multiSelect
        activeOnly
        initialSelectedIds={initialIds}
        preset={preset}
        onConfirm={handleConfirm}
        confirmLabel={saving ? "Salvataggio…" : "Conferma convocazione"}
      />
    </>
  );
}
